import "./Modal.scss";

import { Dialog, Transition } from "@headlessui/react";
import clsx from "clsx";
import { Fragment, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FCCC } from "types";

import { Close } from "@/assets/icons/ds";

const variantClassMapping = {
  light: {
    overlay: "bg-foreground-primary opacity-25",
    panel: "bg-background-primary",
    title: "text-foreground-primary",
    closeIcon: "text-foreground-primary",
    text: "text-foreground-primary",
    description: "modal-description modal-description--light",
  },
  dark: {
    overlay: "bg-surface-secondary backdrop-blur-sm",
    panel: "bg-surface-primary",
    title: "text-foreground-primary-inverse",
    closeIcon: "text-foreground-primary-inverse",
    text: "text-foreground-primary-inverse",
    description: "modal-description modal-description--dark",
  },
};

interface Props {
  title: ReactNode;
  onClose: () => void;
  classNameDescription?: string;
  showCloseButton?: boolean;
  hasScroll?: boolean;
  /** "dark" is the dark dialog variant used for the Welcome Screen auth dialogs. */
  variant?: "light" | "dark";
}

export const Modal: FCCC<Props> = ({
  children,
  className,
  classNameDescription,
  title,
  onClose,
  showCloseButton = true,
  hasScroll = true,
  variant = "light",
}) => {
  const variantClasses = variantClassMapping[variant];
  const { t } = useTranslation();

  const titleType = typeof title === "string" ? "h2" : "div";

  return (
    <Transition.Root as={Fragment} appear show>
      <Dialog
        className="relative z-30"
        as="div"
        onClose={onClose}
        data-testid="modal"
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className={clsx(
              "fixed inset-0 z-10 transition-opacity",
              variantClasses.overlay,
            )}
          />
        </Transition.Child>
        <div id="modal" className="fixed inset-0 z-40 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center tablet:items-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 tablet:translate-y-0 tablet:scale-95"
              enterTo="opacity-100 translate-y-0 tablet:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 tablet:scale-100"
              leaveTo="opacity-0 translate-y-4 tablet:translate-y-0 tablet:scale-95"
            >
              <Dialog.Panel
                className={clsx(
                  "mx-6 my-auto max-w-[520px] overflow-hidden rounded-lg p-6 pr-0 pt-4 tablet:p-8 tablet:pr-0",
                  variantClasses.panel,
                  className,
                )}
              >
                {/*The padding right is set on the lower containers so that the scrollbar is on the very right*/}
                <div className="flex flex-row justify-between gap-2.5 pb-6 pr-6 tablet:pr-8">
                  <Dialog.Title className={variantClasses.title} as={titleType}>
                    {title}
                  </Dialog.Title>
                  {showCloseButton && (
                    <button
                      aria-label={t("common.buttons.abort")}
                      className="modal-close-button flex"
                      onClick={onClose}
                    >
                      <Close
                        className={clsx(
                          "mt-1 w-6 cursor-auto",
                          variantClasses.closeIcon,
                        )}
                      />
                    </button>
                  )}
                </div>
                <Dialog.Description
                  className={clsx(
                    "pr-6 tablet:pr-8",
                    hasScroll &&
                      "max-h-[336px] overflow-y-auto tablet:max-h-[372px]",
                    variantClasses.text,
                    variantClasses.description,
                    classNameDescription,
                  )}
                  as="div"
                >
                  {children}
                </Dialog.Description>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
